import React from "react";
import { Box, Button, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { BarList, Panel } from "../../../ui";
import { C } from "../../../common/theme";
import { RankedRow, formatCount, formatMoney } from "../salesData";

const COLLAPSED_ROWS = 6;

interface BreakdownProps {
  title: string;
  rows: RankedRow[];
  currency: string;
  selected: string[];
  /** Omit to make rows non-interactive */
  onToggle?: (key: string) => void;
  empty?: string;
}

/** Top-N by GMV as a BarList; clicking a row filters the page by it. */
export default function Breakdown({ title, rows, currency, selected, onToggle, empty = "No sales" }: BreakdownProps) {
  const [expanded, setExpanded] = React.useState(false);
  const shown = expanded ? rows : rows.slice(0, COLLAPSED_ROWS);
  const total = rows.reduce((acc, r) => acc + r.value, 0);

  return (
    <Panel sx={{ height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
        <Typography variant="h6" component="h3" sx={{ fontSize: 15 }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: C.muted }}>
          {formatCount(rows.length)}
        </Typography>
      </Box>
      <BarList
        empty={empty}
        max={rows[0]?.value || 1}
        items={shown.map((r) => {
          const isSelected = selected.includes(r.key);
          return {
            key: r.key,
            label: (
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, maxWidth: "100%" }}>
                {isSelected && <CheckIcon sx={{ fontSize: 15, color: C.leaf }} aria-label="Filtered" />}
                <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  {r.label}
                </Box>
              </Box>
            ),
            value: Math.max(0, r.value),
            display: formatMoney(r.value, currency),
            sub: `${formatCount(r.orders)} order${r.orders === 1 ? "" : "s"} · ${total > 0 ? Math.round((r.value / total) * 100) : 0}%`,
            onClick: onToggle ? () => onToggle(r.key) : undefined,
          };
        })}
      />
      {rows.length > COLLAPSED_ROWS && (
        <Button size="small" onClick={() => setExpanded((e) => !e)} sx={{ alignSelf: "flex-start", minHeight: 36 }}>
          {expanded ? "Show fewer" : `Show all ${rows.length}`}
        </Button>
      )}
    </Panel>
  );
}
